import * as Highcharts from 'highcharts';
import { ConfigStore } from './config-store';

/**
 * Build a contrast-first palette instead of only shading.
 * Ensures early series are visually distinguishable.
 */
export function getPalette(primaryColor: string): string[] {
	return [
		primaryColor,              // 1. brand primary
		getComplement(primaryColor), // 2. strong contrast (key fix for x1 vs x2)

		shadeColor(primaryColor, -25), // 3. dark variant
		'#36CFC9',                     // 4. teal (distinct hue shift)
		shadeColor(primaryColor, 25),  // 5. light variant

		'#FFB74D', // 6. orange accent
		'#5B2C6F', // 7. violet contrast
		'#FFD54F', // 8. yellow highlight
		'#6C757D'  // 9. neutral fallback
	];
}

/**
 * Simple complementary color generator (HSL-based approach)
 */
function getComplement(hex: string): string {
	const { h, s, l } = hexToHsl(hex);
	const newHue = (h + 180) % 360;
	return hslToHex(newHue, s, l);
}

/**
 * Lighten / darken utility (kept, but now secondary role)
 */
function shadeColor(color: string, percent: number): string {
	const num = parseInt(color.slice(1), 16),
		amt = Math.round(2.55 * percent),
		R = (num >> 16) + amt,
		G = ((num >> 8) & 0x00FF) + amt,
		B = (num & 0x0000FF) + amt;

	return (
		'#' +
		(
			0x1000000 +
			(R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
			(G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
			(B < 255 ? (B < 1 ? 0 : B) : 255)
		)
			.toString(16)
			.slice(1)
	);
}

/**
 * HEX -> HSL
 */
function hexToHsl(hex: string) {
	const r = parseInt(hex.substring(1, 3), 16) / 255;
	const g = parseInt(hex.substring(3, 5), 16) / 255;
	const b = parseInt(hex.substring(5, 7), 16) / 255;

	const max = Math.max(r, g, b),
		min = Math.min(r, g, b);

	let h = 0,
		s,
		l = (max + min) / 2;

	if (max === min) {
		h = s = 0;
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}

		h *= 60;
	}

	return { h, s, l };
}

/**
 * HSL -> HEX
 */
function hslToHex(h: number, s: number, l: number): string {
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - c / 2;

	let r = 0,
		g = 0,
		b = 0;

	if (h < 60) [r, g, b] = [c, x, 0];
	else if (h < 120) [r, g, b] = [x, c, 0];
	else if (h < 180) [r, g, b] = [0, c, x];
	else if (h < 240) [r, g, b] = [0, x, c];
	else if (h < 300) [r, g, b] = [x, 0, c];
	else [r, g, b] = [c, 0, x];

	return (
		'#' +
		[r, g, b]
			.map(v =>
				Math.round((v + m) * 255)
					.toString(16)
					.padStart(2, '0')
			)
			.join('')
	);
}

/**
 * Apply global Highcharts palette
 */
export function applyHighchartsPalette() {
	const primaryColor: string = ConfigStore.primaryColor().html();

	Highcharts.setOptions({
		colors: getPalette(primaryColor)
	});
}